import { isPublicRoute } from '@/route-access';

import type { z } from 'zod';
import type { Endpoint } from '@alianca/core/contracts/endpoint';

// Cliente fetch tipado fino (T07 §3.2): tipos do contrato Zod, sem parse no cliente (o servidor valida).
// Os tipos são os do fio (`z.input`): `instante` chega como string ISO, como as RPCs entregavam.

export class ApiError extends Error {
    constructor(
        readonly status: number,
        readonly code: string,
        message: string,
        readonly body: Record<string, unknown> | null = null,
    ) {
        super(message);
    }
}

/** Desfecho de uma ação que a tela mostra junto da causa (campo, diálogo). */
export type Resultado = { ok: true } | { ok: false; erro: string };

/** Recusa do servidor (4xx) já vem escrita para a pessoa; falha de rede ou 5xx vira o texto da tela. */
export function mensagemDoServidor(err: unknown, fallback: string): string {
    return err instanceof ApiError && err.status < 500 && err.message ? err.message : fallback;
}

/** Motivo de domínio que o servidor manda junto do erro (`extras.motivo` do `AppError`). */
export const motivoDoErro = (err: unknown): unknown => (err instanceof ApiError ? err.body?.motivo : undefined);

type Campo<E, K extends string> = E extends { [P in K]: infer S extends z.ZodType } ? z.input<S> : undefined;

export type ApiInput<E extends Endpoint> = (Campo<E, 'params'> extends undefined ? unknown : { params: Campo<E, 'params'> }) &
    (Campo<E, 'query'> extends undefined ? unknown : { query: Campo<E, 'query'> }) &
    (Campo<E, 'body'> extends undefined ? { body?: FormData } : { body: Campo<E, 'body'> | FormData });

export type ApiOutput<E extends Endpoint> = z.input<E['output']>;

const REDIRECT_BY_CODE: Record<string, string> = {
    unauthorized: '/login',
    session_expired: '/login',
    user_blocked: '/login?error=blocked',
    registration_pending: '/auth/complete-registration',
    onboarding_pending: '/boas-vindas',
    maintenance: '/manutencao',
};

/** A manutenção leva qualquer página, inclusive as públicas, para a tela de manutenção. */
const REDIRECT_EM_PAGINA_PUBLICA = new Set(['maintenance']);

export function buildUrl(path: string, params?: Record<string, unknown>, query?: Record<string, unknown>): string {
    const filled = path.replace(/:(\w+)/g, (_, k: string) => encodeURIComponent(String(params?.[k])));
    const qs = new URLSearchParams(
        Object.entries(query ?? {})
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => [k, String(v)]),
    ).toString();
    return qs ? `${filled}?${qs}` : filled;
}

type Corpo = Record<string, unknown> | null;

async function enviar(ep: Endpoint, input: unknown): Promise<{ res: Response; json: Corpo }> {
    const { params, query, body } = (input ?? {}) as { params?: Record<string, unknown>; query?: Record<string, unknown>; body?: unknown };
    const isForm = body instanceof FormData;
    const init: RequestInit = {
        method: ep.method,
        credentials: 'same-origin',
        headers: body === undefined || isForm ? undefined : { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    };
    const url = buildUrl(ep.path, params, query);
    const res = await fetch(url, init);
    const json = (await res.json().catch(() => null)) as Corpo;
    return { res, json };
}

function falha(res: Response, json: Corpo): ApiError {
    const code = typeof json?.code === 'string' ? json.code : 'internal';
    const target = REDIRECT_BY_CODE[code];
    // Em página pública (ex.: token de sinistro vencido) o erro vai para a tela, nunca para o /login.
    const { pathname } = window.location;
    if (target && pathname !== target && (REDIRECT_EM_PAGINA_PUBLICA.has(code) || !isPublicRoute(pathname))) window.location.assign(target);
    const message = typeof json?.error === 'string' ? json.error : res.statusText;
    return new ApiError(res.status, code, message, json);
}

/** Endpoint novo: envelope `{ success: true, data }`; devolve `data`. */
export async function api<E extends Endpoint>(ep: E, input: ApiInput<E>): Promise<ApiOutput<E>> {
    const { res, json } = await enviar(ep, input);
    if (res.ok && json?.success === true) return json.data as ApiOutput<E>;
    throw falha(res, json);
}

/** Rota Next preservada byte a byte (L1, `route(..., { legado })`): devolve o corpo inteiro. */
export async function apiLegado<E extends Endpoint>(ep: E, input: ApiInput<E>): Promise<ApiOutput<E>> {
    const { res, json } = await enviar(ep, input);
    if (res.ok) return json as ApiOutput<E>;
    throw falha(res, json);
}
